import {
	durationToUnitsSeconds,
	type EntryIdInput,
	formatEntryId,
} from "./entryId";
import { createAction, type MutationAction } from "./mutationAction";
import {
	type ActionDeps,
	actingHeaders,
	apiCall,
	refreshEntries,
} from "./types";

export type EditPayload = {
	id: EntryIdInput;
	date: string;
	matter: string | null;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	activity: string;
	timekeeperId?: string | null;
};

/**
 * Map the form payload to the v2 PATCH wire shape. Empty strings become null
 * (the v2 merge-patch convention for "clear this field"). `id` is in the URL,
 * not the body; `matter` is display-only and not sent.
 */
export function toUpdateBody(
	entry: EditPayload,
	options: { omitDuration?: boolean } = {},
) {
	return {
		date: entry.date,
		// While a timer runs the server owns this entry's duration, and the form's
		// copy is stale the moment the timer starts. Sending it would read as a
		// deliberate override and rebase the span, discarding tracked time.
		...(options.omitDuration
			? {}
			: { unitsSeconds: durationToUnitsSeconds(entry.duration) }),
		description: entry.description,
		matterId: entry.matter_id || null,
		taskCode: entry.task || null,
		activityCode: entry.activity || null,
	};
}

export function editAction(
	entry: EditPayload,
	deps: ActionDeps,
	options: { omitDuration?: boolean } = {},
): MutationAction {
	// Mirror the optimistic edit into the entries cache. The wire
	// shape is different (camelCase, nested matter/codes), so we map the
	// subset the EditPayload actually carries. Fields the payload doesn't
	// touch (matter description, application, billable, etc.) stay as-is.
	const entryId = formatEntryId(entry.id);
	const rollback = deps.updateEntries((prev) =>
		prev.map((e) => {
			if (e.id !== entryId) return e;
			return {
				...e,
				description: entry.description,
				// Leave duration alone when the timer owns it, or the optimistic
				// update would flash the stale form value over the running total.
				...(options.omitDuration
					? {}
					: {
							units: entry.duration,
							unitsSeconds: durationToUnitsSeconds(entry.duration),
						}),
				matter: e.matter
					? { ...e.matter, id: entry.matter_id || null }
					: entry.matter_id
						? {
								id: entry.matter_id,
								code: null,
								description: entry.matter || null,
								client: null,
							}
						: null,
				codes: {
					...e.codes,
					taskCode: entry.task || null,
					activityCode: entry.activity || null,
				},
			};
		}),
	);

	return createAction({
		execute: () =>
			apiCall(deps, {
				endpoint: `/v2/entries/${entryId}`,
				method: "PATCH",
				body: toUpdateBody(entry, options),
				headers: actingHeaders(entry.timekeeperId),
			}),
		rollback,
		toast: {
			loading: "Saving changes…",
			success: "Entry saved",
			error: "Failed to save entry",
		},
		onSuccess: () => refreshEntries(deps),
		onError: () => refreshEntries(deps),
	});
}
